import { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { adminAPI } from '../../api';
import { useToast } from '../../hooks/useToast';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { SlideOver } from '../../components/ui/SlideOver';
import { MessageSquare, ChevronRight, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

const FEEDBACK_TYPES = [
  { value: '', label: 'All types' },
  { value: 'comment_or_question', label: 'Comment or question' },
  { value: 'bug_report', label: 'Bug report' },
  { value: 'feature_idea', label: 'Feature idea' },
];

const FEEDBACK_STATUSES = [
  { value: '', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const TYPE_LABELS = {
  comment_or_question: 'Comment or question',
  bug_report: 'Bug report',
  feature_idea: 'Feature idea',
};

const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

function truncate(str, maxLen = 60) {
  if (!str) return '';
  return str.length <= maxLen ? str : str.slice(0, maxLen) + '…';
}

function formatUser(ticket) {
  const u = ticket.user;
  if (!u) return '—';
  const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return name ? `${name} (${u.email || '—'})` : (u.email || '—');
}

export default function FeedbackTickets() {
  const { success, error: toastError, ToastContainer } = useToast();
  const [tickets, setTickets] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailStatus, setDetailStatus] = useState('');
  const [detailAdminNotes, setDetailAdminNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setListError('');
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      const data = await adminAPI.getFeedbackTickets(params);
      setTickets(data.tickets || []);
      setPagination((prev) => ({
        ...prev,
        ...(data.pagination || {}),
      }));
    } catch (err) {
      setListError(err?.message || 'Failed to load feedback tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [pagination.page, typeFilter, statusFilter]);

  const openDetail = (ticket) => {
    setSelectedTicket(ticket);
    setDetailStatus(ticket.status);
    setDetailAdminNotes(ticket.adminNotes ?? '');
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedTicket(null);
  };

  const handleSaveDetail = async () => {
    if (!selectedTicket) return;
    try {
      setSaving(true);
      await adminAPI.updateFeedbackTicket(selectedTicket.id, {
        status: detailStatus,
        admin_notes: detailAdminNotes,
      });
      success('Updated', 'Feedback ticket updated.');
      closeDetail();
      fetchTickets();
    } catch (err) {
      toastError('Update failed', err?.message || 'Could not update ticket.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <ToastContainer />
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Feedback</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            View and manage user feedback tickets.
          </p>
        </div>

        <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          <CardHeader className="border-b border-gray-200 dark:border-gray-700">
            <CardTitle className="text-lg text-gray-900 dark:text-gray-100">Tickets</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-4">
              <div className="w-48">
                <Select
                  label="Type"
                  value={typeFilter}
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                  className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {FEEDBACK_TYPES.map((opt) => (
                    <option key={opt.value || 'all'} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="w-48">
                <Select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPagination((p) => ({ ...p, page: 1 }));
                  }}
                  className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {FEEDBACK_STATUSES.map((opt) => (
                    <option key={opt.value || 'all'} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {listError && (
              <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
                {listError}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : tickets.length === 0 ? (
              <div className="py-12 text-center text-gray-500 dark:text-gray-400">
                <MessageSquare className="mx-auto h-10 w-10 mb-2 opacity-50" />
                <p>No feedback tickets match your filters.</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Message
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Page
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Submitter
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Tenant
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-4 py-2 w-10" aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {tickets.map((ticket) => (
                        <tr
                          key={ticket.id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          <td className="px-4 py-2 whitespace-nowrap">
                            <Badge variant="neutral" className="text-xs">
                              {TYPE_LABELS[ticket.type] || ticket.type}
                            </Badge>
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 max-w-xs">
                            {truncate(ticket.message)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 max-w-[140px] truncate" title={ticket.sourcePage || undefined}>
                            {ticket.sourcePage || '—'}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <StatusBadge status={ticket.status} />
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 max-w-[200px] truncate">
                            {formatUser(ticket)}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                            {ticket.tenantName || '—'}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {ticket.createdAt
                              ? format(new Date(ticket.createdAt), 'dd MMM yyyy HH:mm')
                              : '—'}
                          </td>
                          <td className="px-4 py-2">
                            <button
                              type="button"
                              onClick={() => openDetail(ticket)}
                              className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded"
                              aria-label="View details"
                            >
                              <ChevronRight className="h-5 w-5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="mt-4 flex items-center justify-between">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pagination.page <= 1}
                        onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pagination.page >= pagination.totalPages}
                        onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail SlideOver */}
      <SlideOver
        open={detailOpen}
        onOpenChange={(open) => !open && closeDetail()}
        title="Feedback ticket"
        width="w-full max-w-xl"
      >
        {selectedTicket && (
          <div className="space-y-4">
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Type</p>
              <p className="text-sm text-gray-900 dark:text-gray-100">
                {TYPE_LABELS[selectedTicket.type] || selectedTicket.type}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Message</p>
              <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                {selectedTicket.message}
              </p>
            </div>
            {selectedTicket.sourcePage && (
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Page when submitted</p>
                <p className="text-sm text-gray-900 dark:text-gray-100 font-mono break-all">
                  {selectedTicket.sourcePage}
                </p>
              </div>
            )}
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Submitter
              </p>
              <p className="text-sm text-gray-900 dark:text-gray-100">
                {formatUser(selectedTicket)}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Tenant</p>
              <p className="text-sm text-gray-900 dark:text-gray-100">
                {selectedTicket.tenantName || '—'}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Created
              </p>
              <p className="text-sm text-gray-900 dark:text-gray-100">
                {selectedTicket.createdAt
                  ? format(new Date(selectedTicket.createdAt), 'dd MMM yyyy HH:mm')
                  : '—'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={detailStatus}
                onChange={(e) => setDetailStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {FEEDBACK_STATUSES.filter((s) => s.value).map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Admin notes
              </label>
              <Textarea
                value={detailAdminNotes}
                onChange={(e) => setDetailAdminNotes(e.target.value)}
                rows={4}
                placeholder="Internal notes (not visible to user)"
                className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={handleSaveDetail} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Saving…
                  </>
                ) : (
                  'Save changes'
                )}
              </Button>
              <Button variant="outline" onClick={closeDetail} disabled={saving}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </SlideOver>
    </Layout>
  );
}

function StatusBadge({ status }) {
  const variants = {
    open: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    resolved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    closed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  };
  return (
    <Badge className={variants[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}>
      {STATUS_LABELS[status] || status}
    </Badge>
  );
}
